/**
 * Major cargo airports (IATA codes) for the origin/destination box: matches by
 * code, name or country that show instantly while typing, and let a typed code
 * be recognised without a lookup. The airports found near a place come from
 * the backend (`places/resolve/`), which has its own, larger dataset; keep
 * this list's codes present there.
 */
export interface StaticAirport {
  code: string;
  name: string;
  country: string;
}

export const MAJOR_AIRPORTS: StaticAirport[] = [
  // North America — US
  { code: 'PHL', name: 'Philadelphia', country: 'United States' },
  { code: 'JFK', name: 'New York JFK', country: 'United States' },
  { code: 'EWR', name: 'Newark', country: 'United States' },
  { code: 'BOS', name: 'Boston', country: 'United States' },
  { code: 'ORD', name: 'Chicago O’Hare', country: 'United States' },
  { code: 'ATL', name: 'Atlanta', country: 'United States' },
  { code: 'MIA', name: 'Miami', country: 'United States' },
  { code: 'DFW', name: 'Dallas/Fort Worth', country: 'United States' },
  { code: 'IAH', name: 'Houston', country: 'United States' },
  { code: 'LAX', name: 'Los Angeles', country: 'United States' },
  { code: 'SFO', name: 'San Francisco', country: 'United States' },
  { code: 'SEA', name: 'Seattle', country: 'United States' },
  { code: 'MEM', name: 'Memphis', country: 'United States' },
  { code: 'SDF', name: 'Louisville', country: 'United States' },
  { code: 'CVG', name: 'Cincinnati', country: 'United States' },
  { code: 'IND', name: 'Indianapolis', country: 'United States' },
  { code: 'RFD', name: 'Chicago Rockford', country: 'United States' },
  { code: 'BWI', name: 'Baltimore', country: 'United States' },
  { code: 'IAD', name: 'Washington Dulles', country: 'United States' },
  { code: 'DEN', name: 'Denver', country: 'United States' },
  { code: 'PHX', name: 'Phoenix', country: 'United States' },
  { code: 'MSP', name: 'Minneapolis', country: 'United States' },
  { code: 'DTW', name: 'Detroit', country: 'United States' },
  { code: 'RDU', name: 'Raleigh–Durham', country: 'United States' },
  { code: 'SJU', name: 'San Juan', country: 'Puerto Rico' },
  // North America — Canada / Mexico
  { code: 'YYZ', name: 'Toronto', country: 'Canada' },
  { code: 'YUL', name: 'Montréal', country: 'Canada' },
  { code: 'YVR', name: 'Vancouver', country: 'Canada' },
  { code: 'MEX', name: 'Mexico City', country: 'Mexico' },
  { code: 'GDL', name: 'Guadalajara', country: 'Mexico' },
  // Europe
  { code: 'FRA', name: 'Frankfurt', country: 'Germany' },
  { code: 'LEJ', name: 'Leipzig/Halle', country: 'Germany' },
  { code: 'MUC', name: 'Munich', country: 'Germany' },
  { code: 'CGN', name: 'Cologne/Bonn', country: 'Germany' },
  { code: 'HAM', name: 'Hamburg', country: 'Germany' },
  { code: 'AMS', name: 'Amsterdam', country: 'Netherlands' },
  { code: 'LHR', name: 'London Heathrow', country: 'United Kingdom' },
  { code: 'STN', name: 'London Stansted', country: 'United Kingdom' },
  { code: 'EMA', name: 'East Midlands', country: 'United Kingdom' },
  { code: 'CDG', name: 'Paris Charles de Gaulle', country: 'France' },
  { code: 'LGG', name: 'Liège', country: 'Belgium' },
  { code: 'BRU', name: 'Brussels', country: 'Belgium' },
  { code: 'LUX', name: 'Luxembourg', country: 'Luxembourg' },
  { code: 'ZRH', name: 'Zurich', country: 'Switzerland' },
  { code: 'BSL', name: 'Basel–Mulhouse', country: 'Switzerland' },
  { code: 'GVA', name: 'Geneva', country: 'Switzerland' },
  { code: 'VIE', name: 'Vienna', country: 'Austria' },
  { code: 'MXP', name: 'Milan Malpensa', country: 'Italy' },
  { code: 'FCO', name: 'Rome Fiumicino', country: 'Italy' },
  { code: 'MAD', name: 'Madrid', country: 'Spain' },
  { code: 'BCN', name: 'Barcelona', country: 'Spain' },
  { code: 'LIS', name: 'Lisbon', country: 'Portugal' },
  { code: 'DUB', name: 'Dublin', country: 'Ireland' },
  { code: 'CPH', name: 'Copenhagen', country: 'Denmark' },
  { code: 'ARN', name: 'Stockholm', country: 'Sweden' },
  { code: 'OSL', name: 'Oslo', country: 'Norway' },
  { code: 'HEL', name: 'Helsinki', country: 'Finland' },
  { code: 'WAW', name: 'Warsaw', country: 'Poland' },
  { code: 'PRG', name: 'Prague', country: 'Czechia' },
  { code: 'BUD', name: 'Budapest', country: 'Hungary' },
  { code: 'OTP', name: 'Bucharest', country: 'Romania' },
  { code: 'IST', name: 'Istanbul', country: 'Türkiye' },
  { code: 'ATH', name: 'Athens', country: 'Greece' },
  // Middle East
  { code: 'DXB', name: 'Dubai', country: 'United Arab Emirates' },
  { code: 'DWC', name: 'Dubai World Central', country: 'United Arab Emirates' },
  { code: 'AUH', name: 'Abu Dhabi', country: 'United Arab Emirates' },
  { code: 'DOH', name: 'Doha', country: 'Qatar' },
  { code: 'RUH', name: 'Riyadh', country: 'Saudi Arabia' },
  { code: 'JED', name: 'Jeddah', country: 'Saudi Arabia' },
  { code: 'TLV', name: 'Tel Aviv', country: 'Israel' },
  // Asia
  { code: 'HKG', name: 'Hong Kong', country: 'Hong Kong SAR' },
  { code: 'PVG', name: 'Shanghai Pudong', country: 'China' },
  { code: 'PEK', name: 'Beijing Capital', country: 'China' },
  { code: 'CAN', name: 'Guangzhou', country: 'China' },
  { code: 'SZX', name: 'Shenzhen', country: 'China' },
  { code: 'TPE', name: 'Taipei', country: 'Taiwan' },
  { code: 'NRT', name: 'Tokyo Narita', country: 'Japan' },
  { code: 'KIX', name: 'Osaka Kansai', country: 'Japan' },
  { code: 'ICN', name: 'Seoul Incheon', country: 'South Korea' },
  { code: 'SIN', name: 'Singapore', country: 'Singapore' },
  { code: 'BKK', name: 'Bangkok', country: 'Thailand' },
  { code: 'KUL', name: 'Kuala Lumpur', country: 'Malaysia' },
  { code: 'CGK', name: 'Jakarta', country: 'Indonesia' },
  { code: 'MNL', name: 'Manila', country: 'Philippines' },
  { code: 'SGN', name: 'Ho Chi Minh City', country: 'Vietnam' },
  { code: 'HAN', name: 'Hanoi', country: 'Vietnam' },
  { code: 'DEL', name: 'Delhi', country: 'India' },
  { code: 'BOM', name: 'Mumbai', country: 'India' },
  { code: 'MAA', name: 'Chennai', country: 'India' },
  { code: 'BLR', name: 'Bengaluru', country: 'India' },
  { code: 'HYD', name: 'Hyderabad', country: 'India' },
  { code: 'ALA', name: 'Almaty', country: 'Kazakhstan' },
  { code: 'GYD', name: 'Baku', country: 'Azerbaijan' },
  // Oceania
  { code: 'SYD', name: 'Sydney', country: 'Australia' },
  { code: 'MEL', name: 'Melbourne', country: 'Australia' },
  { code: 'AKL', name: 'Auckland', country: 'New Zealand' },
  // South America
  { code: 'GRU', name: 'São Paulo Guarulhos', country: 'Brazil' },
  { code: 'VCP', name: 'Campinas Viracopos', country: 'Brazil' },
  { code: 'EZE', name: 'Buenos Aires', country: 'Argentina' },
  { code: 'SCL', name: 'Santiago', country: 'Chile' },
  { code: 'BOG', name: 'Bogotá', country: 'Colombia' },
  { code: 'UIO', name: 'Quito', country: 'Ecuador' },
  { code: 'LIM', name: 'Lima', country: 'Peru' },
  // Africa
  { code: 'JNB', name: 'Johannesburg', country: 'South Africa' },
  { code: 'CPT', name: 'Cape Town', country: 'South Africa' },
  { code: 'NBO', name: 'Nairobi', country: 'Kenya' },
  { code: 'ADD', name: 'Addis Ababa', country: 'Ethiopia' },
  { code: 'CAI', name: 'Cairo', country: 'Egypt' },
  { code: 'CMN', name: 'Casablanca', country: 'Morocco' },
  { code: 'LOS', name: 'Lagos', country: 'Nigeria' },
];

export function findAirportByCode(
  code?: string | null
): StaticAirport | undefined {
  if (!code) return undefined;
  const needle = code.trim().toUpperCase();
  return MAJOR_AIRPORTS.find((a) => a.code === needle);
}

/** Case-insensitive prefix/substring search over code, city and country. */
export function searchStaticAirports(
  query: string,
  limit = 8
): StaticAirport[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const starts: StaticAirport[] = [];
  const contains: StaticAirport[] = [];
  for (const airport of MAJOR_AIRPORTS) {
    const code = airport.code.toLowerCase();
    const name = airport.name.toLowerCase();
    const country = airport.country.toLowerCase();
    if (code.startsWith(q) || name.startsWith(q)) {
      starts.push(airport);
    } else if (name.includes(q) || country.includes(q)) {
      contains.push(airport);
    }
    if (starts.length >= limit) break;
  }
  return [...starts, ...contains].slice(0, limit);
}
